/**
 * Whether the signed-in user can open Payroll at the current location. Mirrors
 * what the payroll service needs so the portal never offers a page whose
 * requests would fail (e.g. the RollFi SSO 404 "Salon is not configured for
 * payroll" when the location has no RollFi company):
 *
 * - the location must have a RollFi company (`rollfi__company_id`) before
 *   anyone can *use* payroll;
 * - until it does, an Owner of an Active location may still open the page to
 *   see the "Get Started with Payroll" setup;
 * - Owners and Managers use the location-wide views; every other role works
 *   through its own RollFi worker (`rollfi__user_id`), so it needs one.
 *
 * The `settingsPayroll.canAccess` role permission is checked separately (see
 * useNavModel); these rules cover whether payroll is set up for this user.
 * The nav entry, PayrollPage and the SSO page all build from these helpers.
 */
import type { BusinessInfo } from "../types/context";
import type { Employee } from "../types/salon";

export interface PayrollAccessInput {
  currentUser: Pick<Employee, "role" | "rollfi__user_id"> | null | undefined;
  businessInfo:
    | Pick<BusinessInfo, "status" | "rollfi__company_id">
    | null
    | undefined;
}

const isOwnerOrManager = (role: string | undefined): boolean =>
  role === "Owner" || role === "Manager";

/** An Owner who can start payroll setup (the page's marketing view). */
export function canSetUpPayroll({
  currentUser,
  businessInfo,
}: PayrollAccessInput): boolean {
  return (
    currentUser?.role === "Owner" &&
    businessInfo?.status === "Active" &&
    !businessInfo?.rollfi__company_id
  );
}

/** Payroll is set up for this location and for this user. */
export function canUsePayroll({
  currentUser,
  businessInfo,
}: PayrollAccessInput): boolean {
  if (!businessInfo?.rollfi__company_id) return false;
  return isOwnerOrManager(currentUser?.role) || !!currentUser?.rollfi__user_id;
}

/** Whether to offer the Payroll page at all (nav entry). */
export const canOpenPayroll = (input: PayrollAccessInput): boolean =>
  canSetUpPayroll(input) || canUsePayroll(input);
